import { WebPlugin } from "@capacitor/core"
import type {
	GoogleAuthPlugin,
	SignInOptions,
	SignInResponse,
} from "./definitions"

declare global {
	interface Window {
		google?: any
	}
}

const GIS_SRC = "https://accounts.google.com/gsi/client"
const loadGis = () =>
	new Promise<void>((res, rej) => {
		if (window.google) return res()
		const s = document.createElement("script")
		s.src = GIS_SRC
		s.async = s.defer = true
		s.onload = () => res()
		s.onerror = () => rej(new Error("Google Identity Services failed to load"))
		document.head.appendChild(s)
	})

export class GoogleAuthWeb extends WebPlugin implements GoogleAuthPlugin {
	private tokenClient: any // singleton
	private ready = false
	private pending?: {
		resolve: (t: SignInResponse) => void
		reject: (e: unknown) => void
	}

	/** Must be called once (e.g. on app start) */
	public async init(opts: SignInOptions): Promise<void> {
		if (this.ready) return
		await loadGis() /* loads GIS script */
		this.tokenClient = window.google.accounts.oauth2.initTokenClient({
			client_id: opts.clientId,
			scope: "openid email profile",
			ux_mode: "popup",
			/* SINGLE callback – delegates to who-ever is waiting */
			callback: (resp: any) => {
				if (!this.pending) return /* nothing waiting */
				if (resp.error) {
					/* popup closed etc.*/
					this.pending.reject(resp)
				} else {
					this.pending.resolve({
						accessToken: resp.access_token,
					}) /* → return token */
				}
				this.pending = undefined
			},
			/* optional non-OAuth errors: popup blocked / closed early */
			error_callback: (err: any) => this.pending?.reject(err),
		})
		this.ready = true
	}

	/** Opens Google popup and resolves to the access token */
	public async signIn(): Promise<SignInResponse> {
		if (!this.ready) throw new Error("GoogleAuthWeb.init() has not run yet")
		/* Wrap per-call promise */
		return new Promise<SignInResponse>((resolve, reject) => {
			this.pending = { resolve, reject }
			/* No callback allowed here – only overridable fields such as prompt */
			this.tokenClient.requestAccessToken({
				prompt: "consent",
			}) /* triggers popup */
		})
	}

	/** Optional tidy-up */
	public async cleanUp(): Promise<void> {
		window.google?.accounts?.id?.cancel?.()
		document.querySelector(`script[src="${GIS_SRC}"]`)?.remove()
		this.ready = false
		this.tokenClient = undefined
	}
}
